import React, { useContext, useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { ShieldCheck, BadgeDollarSign, Wrench, MapPin } from 'lucide-react';
import Hero from '../components/Hero';
import ProductDetail from '../components/ProductDetail';
import { ThemeContext } from '../context/ThemeContext';
import { useStore } from '../context/ProductStore';

const featuredCategories = [
  { label:'Laptops',     image:'/laptops.jpg',     link:'/shop?category=Laptops' },
  { label:'Desktops',    image:'/desktops.jpg',    link:'/shop?category=Desktops' },
  { label:'Printers',    image:'/printers.jpg',    link:'/shop?category=Printers' },
  { label:'Phones',      image:'/phones.jpg',      link:'/shop?category=Phones' },
  { label:'Networking',  image:'/networking.jpg',  link:'/shop?category=Networking' },
  { label:'PA Systems',  image:'/pa-systems.jpg',  link:'/shop?category=PA Systems' },
  { label:'Accessories', image:'/accessories.jpg', link:'/shop?category=Accessories' },
  { label:'Laptop Bags', image:'/laptop-bags.jpg', link:'/shop?category=Laptop Bags' },
];

const whyUs = [
  { icon:<ShieldCheck size={26} color="#CC0000" strokeWidth={1.8}/>, title:'Quality Products', desc:'We stock only genuine, high-quality electronics from trusted brands and suppliers.' },
  { icon:<BadgeDollarSign size={26} color="#CC0000" strokeWidth={1.8}/>, title:'Competitive Prices', desc:'Get the best value for your money with our competitive and transparent pricing.' },
  { icon:<Wrench size={26} color="#CC0000" strokeWidth={1.8}/>, title:'Expert Service', desc:'Our knowledgeable team provides professional advice and after-sales support.' },
  { icon:<MapPin size={26} color="#CC0000" strokeWidth={1.8}/>, title:'Convenient Location', desc:'Find us at B30 Eastgate Market, right in the heart of Harare.' },
];

const AUDIENCE_TABS = ['Business', 'Executive', 'Students', 'Gaming'];

const ForTagShowcaseCard = ({ item, tag, isDark, allItems = [], itemIndex = 0 }) => {
  const [showDetail, setShowDetail] = useState(false);
  const [hovered,    setHovered]    = useState(false);
  const [expanded,   setExpanded]   = useState(false);
  const [showZoom,   setShowZoom]   = useState(false);

  const images = item.images?.length ? item.images : (item.image ? [item.image] : []);
  const [activeImg, setActiveImg] = useState(0);
  const [zoomImg,   setZoomImg]   = useState(0);
  const [detailIndex, setDetailIndex] = useState(itemIndex);
  const [detailActiveImg, setDetailActiveImg] = useState(0);

  const currentItem   = allItems[detailIndex] || item;
  const currentImages = currentItem.images?.length ? currentItem.images : (currentItem.image ? [currentItem.image] : []);
  const currentBullets = currentItem.description ? currentItem.description.split('.').filter(s => s.trim()) : [];

  const prevItem = () => { if (detailIndex > 0) { setDetailIndex(i => i-1); setDetailActiveImg(0); } };
  const nextItem = () => { if (detailIndex < allItems.length-1) { setDetailIndex(i => i+1); setDetailActiveImg(0); } };
  const prevImg  = () => setDetailActiveImg(i => (i-1+currentImages.length) % currentImages.length);
  const nextImg  = () => setDetailActiveImg(i => (i+1) % currentImages.length);

  React.useEffect(() => {
    if (!showDetail && !showZoom) return;
    const handler = (e) => {
      if (e.key === 'ArrowLeft')  showZoom ? setZoomImg(i => (i-1+currentImages.length)%currentImages.length) : prevImg();
      if (e.key === 'ArrowRight') showZoom ? setZoomImg(i => (i+1)%currentImages.length) : nextImg();
      if (e.key === 'Escape')     { setShowZoom(false); setShowDetail(false); }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [showDetail, showZoom, currentImages.length]);

  const bullets = item.description ? item.description.split('.').filter(s => s.trim()) : [];
  const COLLAPSED_COUNT = 2;
  const visibleBullets  = expanded ? bullets : bullets.slice(0, COLLAPSED_COUNT);
  const hasMore         = bullets.length > COLLAPSED_COUNT;

  return (
    <>
      <style>{`
        @media (max-width: 900px) { .for-tag-page { flex-direction: column !important; gap: 20px !important; } }
        .ftg-thumb:hover { border-color: #CC0000 !important; opacity: 1 !important; }
        .ftg-arr:hover { background: rgba(0,0,0,0.7) !important; }
        .ftg-item-arr:hover { background: #CC0000 !important; color: #fff !important; }
      `}</style>

      {/* ── Card ── */}
      <div
        onClick={() => { setShowDetail(true); setDetailIndex(itemIndex); setDetailActiveImg(0); }}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => { setHovered(false); setExpanded(false); }}
        style={{ position:'relative', backgroundColor: '#fff', borderRadius:'4px', overflow:'hidden', cursor:'pointer', border:`1px solid ${hovered?'#CC0000':(isDark?'rgba(255,255,255,0.08)':'#e5e7eb')}`, display:'flex', flexDirection:'column', transition:'all 0.25s ease', transform: hovered?'translateY(-4px)':'translateY(0)', boxShadow: hovered?'0 8px 30px rgba(204,0,0,0.15)':'0 1px 3px rgba(0,0,0,0.06)', zIndex: hovered?5:1 }}
      >
        <div style={{ display:'flex', alignItems:'center', justifyContent:'center', padding:'24px 24px 16px', aspectRatio:'4/3', overflow:'hidden', backgroundColor: '#fff', position:'relative' }}>
          <img src={images[0] || item.image} alt={item.title||tag} style={{ maxWidth:'100%', maxHeight:'100%', objectFit:'contain', display:'block', transition:'transform 0.35s ease', transform: hovered?'scale(1.09)':'scale(1)' }} />
          {hovered && <span style={{ position:'absolute', bottom:'8px', right:'12px', fontSize:'18px', color: isDark?'rgba(255,255,255,0.5)':'#9ca3af' }}>⇄</span>}
        </div>
        <div style={{ height:'1px', background: isDark?'rgba(255,255,255,0.06)':'#f3f4f6', margin:'0 16px' }} />
        <div style={{ padding:'16px', display:'flex', flexDirection:'column', gap:'6px' }}>
          <h3 style={{ fontSize:'16px', fontWeight:'700', color: isDark?'#fff':'#111', lineHeight:1.4, margin:0, fontFamily:"'League Spartan', sans-serif" }}>{item.title||tag}</h3>
          <p style={{ fontSize:'13px', color:'rgba(128,128,128,0.9)', margin:0, fontFamily:"'Inter', sans-serif" }}>{tag}</p>
          {hovered && bullets.length > 0 && (
            <div style={{ display:'flex', flexDirection:'column', gap:'6px', marginTop:'4px' }}>
              {visibleBullets.map((line, i) => (
                <div key={i} style={{ display:'flex', gap:'8px', alignItems:'flex-start' }}>
                  <span style={{ color: isDark?'rgba(255,255,255,0.5)':'#374151', flexShrink:0, fontSize:'15px', marginTop:'1px' }}>•</span>
                  <span style={{ fontSize:'14px', color: isDark?'rgba(255,255,255,0.75)':'#374151', lineHeight:1.6, fontFamily:"'Inter', sans-serif", fontWeight:400 }}>{line.trim()}</span>
                </div>
              ))}
              {hasMore && (
                <button onClick={e => { e.preventDefault(); e.stopPropagation(); setExpanded(v=>!v); }}
                  style={{ background:'none', border:'none', padding:'2px 0', cursor:'pointer', fontSize:'12px', fontWeight:'700', color:'#CC0000', fontFamily:"'Inter', sans-serif", textAlign:'left' }}>
                  {expanded ? '▲ Show less' : `▼ Read more (${bullets.length - COLLAPSED_COUNT} more)`}
                </button>
              )}
            </div>
          )}
          {hovered && (
            <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', gap:'12px', marginTop:'8px' }}>
              <span style={{ fontSize:'20px', color: isDark?'rgba(255,255,255,0.6)':'#374151' }}>♡</span>
              <button onClick={e => { e.preventDefault(); e.stopPropagation(); setShowDetail(true); setDetailIndex(itemIndex); setDetailActiveImg(0); }}
                style={{ flex:1, textAlign:'center', padding:'10px 16px', borderRadius:'4px', border:'none', background:'#1a73e8', color:'#fff', fontWeight:'700', fontSize:'13px', fontFamily:"'League Spartan', sans-serif", cursor:'pointer' }}>
                READ MORE
              </button>
              <span style={{ fontSize:'18px', color: isDark?'rgba(255,255,255,0.6)':'#374151' }}>🔍</span>
            </div>
          )}
        </div>
      </div>

      {/* ── Full Detail Modal ── */}
      {showDetail && (
        <div style={{ position:'fixed', inset:0, zIndex:9990, backgroundColor: '#fff', overflowY:'auto' }}>
          <button onClick={() => setShowDetail(false)}
            style={{ position:'fixed', top:16, right:16, width:36, height:36, borderRadius:'50%', border:'none', background:'rgba(0,0,0,0.55)', color:'#fff', fontSize:18, cursor:'pointer', zIndex:10 }}>✕</button>

          {allItems.length > 1 && (
            <>
              <button className="ftg-item-arr" onClick={prevItem} disabled={detailIndex===0}
                style={{ position:'fixed', top:'50%', left:12, transform:'translateY(-50%)', width:40, height:40, borderRadius:'50%', border:'1px solid #e5e7eb', background:'#fff', color:'#111', fontSize:22, cursor: detailIndex===0?'default':'pointer', zIndex:10, display:'flex', alignItems:'center', justifyContent:'center', opacity: detailIndex===0?0.3:1, transition:'all 0.15s' }}>‹</button>
              <button className="ftg-item-arr" onClick={nextItem} disabled={detailIndex===allItems.length-1}
                style={{ position:'fixed', top:'50%', right:12, transform:'translateY(-50%)', width:40, height:40, borderRadius:'50%', border:'1px solid #e5e7eb', background:'#fff', color:'#111', fontSize:22, cursor: detailIndex===allItems.length-1?'default':'pointer', zIndex:10, display:'flex', alignItems:'center', justifyContent:'center', opacity: detailIndex===allItems.length-1?0.3:1, transition:'all 0.15s' }}>›</button>
            </>
          )}

          <div style={{ maxWidth:1280, margin:'0 auto', padding:'24px clamp(12px,3vw,32px) 60px' }}>
            <div style={{ fontSize:13, color: isDark?'rgba(255,255,255,0.5)':'#6b7280', fontFamily:"'Inter', sans-serif", marginBottom:24 }}>
              <Link to="/" style={{ color:'inherit', textDecoration:'none' }}>Home</Link>{' / '}
              <Link to={`/shop?for=${encodeURIComponent(tag)}`} style={{ color: isDark?'rgba(255,255,255,0.7)':'#374151', textDecoration:'none', fontWeight:600 }}>{tag}</Link>
              {' / '}<span style={{ color: isDark?'#fff':'#111', fontWeight:700 }}>{currentItem.title||tag}</span>
            </div>

            <div className="for-tag-page" style={{ display:'flex', gap:40 }}>
              {/* LEFT — gallery */}
              <div style={{ flex:'1 1 50%', minWidth:0, display:'flex', gap:14 }}>
                {/* Thumbnail strip */}
                {currentImages.length > 1 && (
                  <div style={{ display:'flex', flexDirection:'column', gap:8, flexShrink:0, width:72 }}>
                    {currentImages.map((img, i) => (
                      <div key={i} className="ftg-thumb" onClick={() => setDetailActiveImg(i)}
                        style={{ width:72, height:72, borderRadius:6, overflow:'hidden', border:`2px solid ${detailActiveImg===i?'#CC0000':'#e5e7eb'}`, backgroundColor: '#f7f7f7', display:'flex', alignItems:'center', justifyContent:'center', cursor:'pointer', opacity: detailActiveImg===i?1:0.6, transition:'all 0.15s', flexShrink:0 }}>
                        <img src={img} alt={`${currentItem.title||tag} ${i+1}`} style={{ width:'100%', height:'100%', objectFit:'contain' }} />
                      </div>
                    ))}
                  </div>
                )}

                {/* Main image — click to open fullscreen lightbox */}
                <div
                  style={{ position:'relative', flex:1, minWidth:0, aspectRatio:'4/3', borderRadius:8, overflow:'hidden', backgroundColor: '#f7f7f7', display:'flex', alignItems:'center', justifyContent:'center', cursor:'zoom-in' }}
                  onClick={() => { setZoomImg(detailActiveImg); setShowZoom(true); }}
                >
                  <img src={currentImages[detailActiveImg] || currentItem.image} alt={currentItem.title||tag}
                    style={{ width:'100%', height:'100%', objectFit:'contain', transition:'transform 0.3s ease', userSelect:'none' }}
                    onMouseEnter={e => e.currentTarget.style.transform='scale(1.04)'}
                    onMouseLeave={e => e.currentTarget.style.transform='scale(1)'}
                  />

                  {/* Prev/Next arrows */}
                  {currentImages.length > 1 && (
                    <button className="ftg-arr" onClick={e => { e.stopPropagation(); prevImg(); }}
                      style={{ position:'absolute', top:'50%', left:10, transform:'translateY(-50%)', width:36, height:36, borderRadius:'50%', border:'none', background:'rgba(0,0,0,0.45)', color:'#fff', fontSize:20, cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', zIndex:5 }}>‹</button>
                  )}
                  {currentImages.length > 1 && (
                    <button className="ftg-arr" onClick={e => { e.stopPropagation(); nextImg(); }}
                      style={{ position:'absolute', top:'50%', right:10, transform:'translateY(-50%)', width:36, height:36, borderRadius:'50%', border:'none', background:'rgba(0,0,0,0.45)', color:'#fff', fontSize:20, cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', zIndex:5 }}>›</button>
                  )}
                  {currentImages.length > 1 && (
                    <div style={{ position:'absolute', bottom:10, left:'50%', transform:'translateX(-50%)', background:'rgba(0,0,0,0.5)', color:'#fff', fontSize:12, fontWeight:600, padding:'3px 10px', borderRadius:100, fontFamily:"'Inter', sans-serif", zIndex:5, pointerEvents:'none' }}>
                      {detailActiveImg+1} / {currentImages.length}
                    </div>
                  )}
                  {/* Click to expand hint */}
                  <div style={{ position:'absolute', bottom:10, left:10, fontSize:11, color:'rgba(255,255,255,0.7)', background:'rgba(0,0,0,0.45)', padding:'3px 8px', borderRadius:6, pointerEvents:'none', zIndex:5, fontFamily:"'Inter', sans-serif" }}>
                    🔍 Click to expand
                  </div>
                </div>
              </div>

              {/* RIGHT — info */}
              <div style={{ flex:'1 1 50%', minWidth:0, display:'flex', flexDirection:'column', gap:12 }}>
                <h1 style={{ fontSize:'clamp(22px,3vw,32px)', fontWeight:'800', color: isDark?'#fff':'#111', margin:0, fontFamily:"'League Spartan', sans-serif", lineHeight:1.25 }}>{currentItem.title||tag}</h1>
                <div style={{ display:'flex', alignItems:'center', gap:20, paddingBottom:14, borderBottom:`1px solid ${isDark?'rgba(255,255,255,0.08)':'#e5e7eb'}` }}>
                  <span style={{ fontSize:14, fontWeight:700, color: isDark?'rgba(255,255,255,0.8)':'#374151' }}>⇄ Add to compare</span>
                  <span style={{ fontSize:14, fontWeight:700, color: isDark?'rgba(255,255,255,0.8)':'#374151' }}>♡ Add to wishlist</span>
                </div>
                <div style={{ fontSize:14, color: isDark?'rgba(255,255,255,0.7)':'#374151' }}>
                  <strong style={{ color: isDark?'#fff':'#111' }}>Categories:</strong>{' '}
                  <span style={{ color: isDark?'rgba(255,255,255,0.55)':'#6b7280' }}>{tag}</span>
                </div>
                {allItems.length > 1 && (
                  <div style={{ fontSize:12, color: isDark?'rgba(255,255,255,0.4)':'#9ca3af', fontFamily:"'Inter', sans-serif" }}>Item {detailIndex+1} of {allItems.length}</div>
                )}
                <Link to={`/shop?for=${encodeURIComponent(tag)}`}
                  style={{ marginTop:8, textAlign:'center', padding:'14px 16px', borderRadius:4, border:'none', background:'#CC0000', color:'#fff', fontWeight:700, fontSize:15, fontFamily:"'League Spartan', sans-serif", textDecoration:'none', display:'block', maxWidth:320 }}>
                  View all {tag} products →
                </Link>
              </div>
            </div>

            {/* Description */}
            <div style={{ marginTop:40, borderBottom:`1px solid ${isDark?'rgba(255,255,255,0.08)':'#e5e7eb'}`, paddingBottom:14 }}>
              <span style={{ fontSize:13, fontWeight:800, letterSpacing:'0.04em', color: isDark?'#fff':'#111', borderBottom:'2px solid #1a73e8', paddingBottom:14 }}>DESCRIPTION</span>
            </div>
            <div style={{ paddingTop:28, display:'flex', flexDirection:'column', gap:14 }}>
              <h2 style={{ fontSize:'clamp(18px,2.2vw,26px)', fontWeight:800, color: isDark?'#fff':'#111', margin:0, fontFamily:"'League Spartan', sans-serif" }}>{currentItem.title||tag}</h2>
              {currentItem.description && (
                <ul style={{ margin:0, padding:0, listStyle:'none', display:'flex', flexDirection:'column', gap:12 }}>
                  {currentBullets.map((line, i) => (
                    <li key={i} style={{ display:'flex', gap:10, alignItems:'flex-start', fontSize:15, color: isDark?'rgba(255,255,255,0.75)':'#374151', lineHeight:1.7, fontFamily:"'Inter', sans-serif" }}>
                      <span style={{ flexShrink:0, marginTop:8, width:6, height:6, borderRadius:'50%', backgroundColor: isDark?'rgba(255,255,255,0.4)':'#9ca3af' }} />
                      <span>{line.trim()}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ── Zoom Lightbox — fullscreen, black bg, icons top-right, side arrows ── */}
      {showZoom && (() => {
        const ZoomBox = () => {
          const [scale, setScale] = React.useState(1);
          const zoomIn  = (e) => { e.stopPropagation(); setScale(s => Math.min(s + 0.5, 4)); };
          const zoomOut = (e) => { e.stopPropagation(); setScale(s => Math.max(s - 0.5, 1)); };
          const reset   = (e) => { e.stopPropagation(); setScale(1); };
          return (
            <div
              style={{ position:'fixed', inset:0, zIndex:9999, backgroundColor:'#000', display:'flex', alignItems:'center', justifyContent:'center' }}
              onClick={() => { setShowZoom(false); }}
            >
              {/* Top-right icons */}
              <div style={{ position:'fixed', top:16, right:16, display:'flex', gap:8, zIndex:10 }} onClick={e => e.stopPropagation()}>
                {/* Zoom IN */}
                <button onClick={zoomIn} title="Zoom in"
                  style={{ width:36, height:36, borderRadius:'50%', border:'1px solid rgba(255,255,255,0.2)', background:'rgba(255,255,255,0.08)', color:'#fff', cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center' }}
                  onMouseEnter={e => e.currentTarget.style.background='rgba(255,255,255,0.22)'}
                  onMouseLeave={e => e.currentTarget.style.background='rgba(255,255,255,0.08)'}>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/><line x1="11" y1="8" x2="11" y2="14"/><line x1="8" y1="11" x2="14" y2="11"/></svg>
                </button>
                {/* Zoom OUT */}
                <button onClick={zoomOut} title="Zoom out" disabled={scale <= 1}
                  style={{ width:36, height:36, borderRadius:'50%', border:'1px solid rgba(255,255,255,0.2)', background:'rgba(255,255,255,0.08)', color: scale<=1?'rgba(255,255,255,0.3)':'#fff', cursor: scale<=1?'default':'pointer', display:'flex', alignItems:'center', justifyContent:'center' }}
                  onMouseEnter={e => { if(scale>1) e.currentTarget.style.background='rgba(255,255,255,0.22)'; }}
                  onMouseLeave={e => e.currentTarget.style.background='rgba(255,255,255,0.08)'}>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/><line x1="8" y1="11" x2="14" y2="11"/></svg>
                </button>
                {/* Reset */}
                {scale !== 1 && (
                  <button onClick={reset} title="Reset size"
                    style={{ width:36, height:36, borderRadius:'50%', border:'1px solid rgba(255,255,255,0.2)', background:'rgba(255,255,255,0.08)', color:'#fff', cursor:'pointer', fontSize:12, fontWeight:700, fontFamily:"'Inter', sans-serif" }}
                    onMouseEnter={e => e.currentTarget.style.background='rgba(255,255,255,0.22)'}
                    onMouseLeave={e => e.currentTarget.style.background='rgba(255,255,255,0.08)'}>1:1</button>
                )}
                {/* Close */}
                <button onClick={() => setShowZoom(false)}
                  style={{ width:36, height:36, borderRadius:'50%', border:'1px solid rgba(255,255,255,0.2)', background:'rgba(255,255,255,0.08)', color:'#fff', fontSize:18, cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center' }}
                  onMouseEnter={e => e.currentTarget.style.background='rgba(255,255,255,0.22)'}
                  onMouseLeave={e => e.currentTarget.style.background='rgba(255,255,255,0.08)'}>✕</button>
              </div>

              {/* Scale indicator */}
              {scale > 1 && (
                <div style={{ position:'fixed', top:16, left:16, color:'rgba(255,255,255,0.5)', fontSize:12, fontFamily:"'Inter', sans-serif", zIndex:10 }}>
                  {Math.round(scale * 100)}%
                </div>
              )}

              {/* Left arrow */}
              {currentImages.length > 1 && (
                <button onClick={e => { e.stopPropagation(); setZoomImg(i => (i-1+currentImages.length)%currentImages.length); }}
                  style={{ position:'fixed', top:'50%', left:16, transform:'translateY(-50%)', width:44, height:44, borderRadius:'50%', border:'1px solid rgba(255,255,255,0.15)', background:'rgba(255,255,255,0.08)', color:'#fff', fontSize:26, cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', zIndex:10 }}
                  onMouseEnter={e => e.currentTarget.style.background='rgba(255,255,255,0.2)'}
                  onMouseLeave={e => e.currentTarget.style.background='rgba(255,255,255,0.08)'}>‹</button>
              )}

              {/* Image panel */}
              <div
                onClick={e => e.stopPropagation()}
                style={{ backgroundColor:'#fff', borderRadius: scale>1?0:4, padding: scale>1?0:'24px', width: scale>1?'100vw':'78vw', maxHeight: scale>1?'100vh':'92vh', display:'flex', alignItems:'center', justifyContent:'center', overflow: scale>1?'auto':'hidden', transition:'all 0.2s ease', boxShadow: scale<=1?'0 0 60px rgba(0,0,0,0.5)':'none' }}
              >
                <img
                  src={currentImages[zoomImg] || currentItem.image}
                  alt={currentItem.title||tag}
                  style={{ maxWidth: scale>1?'none':'100%', maxHeight: scale>1?'none':'88vh', width: scale>1?`${scale*500}px`:'auto', height: scale<=1?'88vh':'auto', objectFit:'contain', display:'block', transition:'width 0.25s ease', userSelect:'none', cursor: scale < 4 ? 'zoom-in' : 'zoom-out' }}
                  onDoubleClick={e => { e.stopPropagation(); setScale(s => s < 4 ? Math.min(s + 0.75, 4) : 1); }}
                />
              </div>

              {/* Right arrow */}
              {currentImages.length > 1 && (
                <button onClick={e => { e.stopPropagation(); setZoomImg(i => (i+1)%currentImages.length); }}
                  style={{ position:'fixed', top:'50%', right:16, transform:'translateY(-50%)', width:44, height:44, borderRadius:'50%', border:'1px solid rgba(255,255,255,0.15)', background:'rgba(255,255,255,0.08)', color:'#fff', fontSize:26, cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', zIndex:10 }}
                  onMouseEnter={e => e.currentTarget.style.background='rgba(255,255,255,0.2)'}
                  onMouseLeave={e => e.currentTarget.style.background='rgba(255,255,255,0.08)'}>›</button>
              )}

              {/* Counter */}
              {currentImages.length > 1 && (
                <div style={{ position:'fixed', bottom:20, left:'50%', transform:'translateX(-50%)', color:'rgba(255,255,255,0.55)', fontSize:13, fontFamily:"'Inter', sans-serif", zIndex:10 }}>
                  {zoomImg+1} / {currentImages.length}
                </div>
              )}
            </div>
          );
        };
        return <ZoomBox />;
      })()}
    </>
  );
};

const SimpleProductCard = ({ product, isDark }) => {
  const { trackView } = useStore();
  const [showDetail, setShowDetail] = useState(false);
  const [hovered,    setHovered]    = useState(false);
  const [expanded,   setExpanded]   = useState(false);

  const image = product.images?.[0] || product.image || '';
  const title = product.title || product.name || '';

  const bullets = product.description
    ? product.description.split('.').filter(s => s.trim())
    : [];
  const COLLAPSED_COUNT = 2;
  const visibleBullets  = expanded ? bullets : bullets.slice(0, COLLAPSED_COUNT);
  const hasMore         = bullets.length > COLLAPSED_COUNT;

  const handleClick = () => { trackView(product.id); setShowDetail(true); };

  return (
    <>
      <div
        onClick={handleClick}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => { setHovered(false); setExpanded(false); }}
        style={{ position:'relative', backgroundColor: '#ffffff', borderRadius:'4px', overflow:'hidden', cursor:'pointer', border:`1px solid ${hovered ? '#CC0000' : (isDark ? 'rgba(255,255,255,0.08)' : '#e5e7eb')}`, display:'flex', flexDirection:'column', transition:'all 0.25s ease', transform: hovered ? 'translateY(-4px)' : 'translateY(0)', boxShadow: hovered ? '0 8px 30px rgba(204,0,0,0.15)' : '0 1px 3px rgba(0,0,0,0.06)', zIndex: hovered ? 5 : 1 }}
      >
        <div style={{ display:'flex', alignItems:'center', justifyContent:'center', padding:'24px 24px 16px', aspectRatio:'4/3', overflow:'hidden', backgroundColor: '#ffffff', position:'relative' }}>
          {image ? (
            <img src={image} alt={title} style={{ maxWidth:'100%', maxHeight:'100%', objectFit:'contain', display:'block', transition:'transform 0.35s ease', transform: hovered ? 'scale(1.09)' : 'scale(1)', mixBlendMode:'multiply' }} />
          ) : (
            <span style={{ fontSize:'40px' }}>📦</span>
          )}
          {hovered && <span style={{ position:'absolute', bottom:'8px', right:'12px', fontSize:'18px', color: isDark ? 'rgba(255,255,255,0.5)' : '#9ca3af' }}>⇄</span>}
        </div>
        <div style={{ height:'1px', background: isDark ? 'rgba(255,255,255,0.06)' : '#f3f4f6', margin:'0 16px' }} />
        <div style={{ padding:'16px', display:'flex', flexDirection:'column', gap:'6px' }}>
          <h3 style={{ fontSize:'16px', fontWeight:'700', color: '#111111', lineHeight:1.4, margin:0, fontFamily:"'League Spartan', sans-serif" }}>{title}</h3>
          <p style={{ fontSize:'13px', color:'rgba(128,128,128,0.9)', margin:0, fontFamily:"'Inter', sans-serif" }}>{product.category}{product.brand ? `, ${product.brand}` : ''}</p>
          {hovered && bullets.length > 0 && (
            <div style={{ display:'flex', flexDirection:'column', gap:'6px', marginTop:'4px' }}>
              {visibleBullets.map((line, i) => (
                <div key={i} style={{ display:'flex', gap:'8px', alignItems:'flex-start' }}>
                  <span style={{ color: isDark ? 'rgba(255,255,255,0.5)' : '#374151', flexShrink:0, fontSize:'15px', marginTop:'1px' }}>•</span>
                  <span style={{ fontSize:'14px', color: isDark ? 'rgba(255,255,255,0.75)' : '#374151', lineHeight:1.6, fontFamily:"'Inter', sans-serif", fontWeight:400 }}>{line.trim()}</span>
                </div>
              ))}
              {hasMore && (
                <button onClick={(e) => { e.preventDefault(); e.stopPropagation(); setExpanded(v => !v); }}
                  style={{ background:'none', border:'none', padding:'2px 0', cursor:'pointer', fontSize:'12px', fontWeight:'700', color:'#CC0000', fontFamily:"'Inter', sans-serif", letterSpacing:'0.02em', textAlign:'left' }}>
                  {expanded ? '▲ Show less' : `▼ Read more (${bullets.length - COLLAPSED_COUNT} more)`}
                </button>
              )}
            </div>
          )}
          {hovered && (
            <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', gap:'12px', marginTop:'8px' }}>
              <span style={{ fontSize:'20px', color: isDark ? 'rgba(255,255,255,0.6)' : '#374151' }}>♡</span>
              <button onClick={(e) => { e.preventDefault(); e.stopPropagation(); handleClick(); }}
                style={{ flex:1, textAlign:'center', padding:'10px 16px', borderRadius:'4px', border:'none', background:'#1a73e8', color:'#ffffff', fontWeight:'700', fontSize:'13px', letterSpacing:'0.02em', fontFamily:"'League Spartan', sans-serif", cursor:'pointer' }}>
                READ MORE
              </button>
              <span style={{ fontSize:'18px', color: isDark ? 'rgba(255,255,255,0.6)' : '#374151' }}>🔍</span>
            </div>
          )}
        </div>
      </div>
      {showDetail && <ProductDetail product={product} onClose={() => setShowDetail(false)} />}
    </>
  );
};

const Home = () => {
  const { theme } = useContext(ThemeContext);
  const { products, forTagBanners } = useStore();
  const isDark = theme === 'dark';
  const [activeTab, setActiveTab] = useState('Business');
  const allProducts = (products || []).filter(p => !p.outOfStock);

  const featuredProducts = useMemo(() => {
    const bannerCount = (forTagBanners?.[activeTab] || []).length;
    const limit = Math.max(0, 8 - bannerCount);
    const tagged = allProducts.filter(p => (p.forTags || []).includes(activeTab));
    if (tagged.length > 0) return tagged.slice(0, limit);
    return allProducts.slice(0, limit);
  }, [allProducts, activeTab, forTagBanners]);

  const cycleTab = (dir) => {
    setActiveTab(prev => {
      const idx = AUDIENCE_TABS.indexOf(prev);
      let nextIdx = idx + dir;
      if (nextIdx < 0) nextIdx = AUDIENCE_TABS.length - 1;
      if (nextIdx >= AUDIENCE_TABS.length) nextIdx = 0;
      return AUDIENCE_TABS[nextIdx];
    });
  };

  return (
    <>
      <style>{`
        .home-cat-grid { display:grid; grid-template-columns:repeat(8,1fr); border-radius:8px; overflow:hidden; }
        @media(max-width:900px){ .home-cat-grid{ grid-template-columns:repeat(4,1fr); } }
        @media(max-width:480px){ .home-cat-grid{ grid-template-columns:repeat(4,1fr); } }
        .home-for-bar { display:flex; align-items:center; gap:14px; border-bottom:1px solid rgba(128,128,128,0.2); margin-bottom:24px; }
        .home-for-tabs { display:flex; align-items:center; gap:28px; flex:1 1 auto; min-width:0; overflow-x:auto; white-space:nowrap; scrollbar-width:none; }
        .home-for-tabs::-webkit-scrollbar { display:none; }
        .home-for-label { font-size:clamp(20px,3vw,28px); font-weight:900; letter-spacing:-0.01em; flex-shrink:0; }
        .home-for-tab { background:none; border:none; cursor:pointer; font-size:clamp(13px,1.5vw,16px); font-weight:700; letter-spacing:0.04em; padding:0 0 12px; border-bottom:2px solid transparent; transition:color 0.2s,border-color 0.2s; flex-shrink:0; }
        .home-for-arrows { display:flex; gap:8px; flex-shrink:0; margin-left:auto; padding-left:12px; padding-bottom:12px; }
        .home-for-arrow-btn { width:32px; height:32px; border-radius:50%; cursor:pointer; font-size:18px; font-weight:900; display:flex; align-items:center; justify-content:center; padding:0; line-height:1; transition:background 0.2s; }
        .home-product-grid { display:grid; grid-template-columns:repeat(4,1fr); gap:16px; }
        @media(max-width:900px){ .home-product-grid{ grid-template-columns:repeat(2,1fr); } }
        @media(max-width:480px){ .home-product-grid{ grid-template-columns:repeat(2,1fr); gap:12px; } }
        .home-why-grid { display:grid; grid-template-columns:repeat(4,1fr); gap:20px; }
        @media(max-width:900px){ .home-why-grid{ grid-template-columns:repeat(2,1fr); } }
        @media(max-width:480px){ .home-why-grid{ grid-template-columns:1fr; gap:14px; } }
        .home-cta-btns { display:flex; gap:14px; flex-wrap:wrap; justify-content:center; }
        .home-cta-btns a { flex:1; min-width:140px; max-width:220px; text-align:center; }
      `}</style>

      <main style={{ backgroundColor: '#ffffff', color: '#111111', minHeight:'100vh', paddingTop:'98px' }}>

        <Hero />

        {/* OUR PRODUCTS */}
        <section style={{ backgroundColor: '#f7f7f7', padding:'clamp(28px,4vw,48px) 0 24px' }}>
          <div style={{ maxWidth:'1280px', margin:'0 auto', padding:'0 clamp(12px,3vw,24px)' }}>
            <h2 style={{ fontSize:'clamp(17px,2.5vw,26px)', fontWeight:'700', color: '#111111', marginBottom:'20px', letterSpacing:'-0.01em' }}>Our Products</h2>
            <div className="home-cat-grid" style={{ boxShadow:'0 1px 8px rgba(0,0,0,0.08)', backgroundColor: '#ffffff' }}>
              {featuredCategories.map((cat, i) => (
                <Link key={cat.label} to={cat.link}
                  style={{ display:'flex', flexDirection:'column', alignItems:'center', padding:'20px 8px 14px', textDecoration:'none', borderRight: i < featuredCategories.length-1 ? `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)'}` : 'none', transition:'background 0.2s', backgroundColor:'transparent', cursor:'pointer' }}
                  onMouseEnter={e => e.currentTarget.style.backgroundColor = isDark ? '#2d2d2d' : '#f5f5f5'}
                  onMouseLeave={e => e.currentTarget.style.backgroundColor = 'transparent'}>
                  <div style={{ width:'100%', height:'80px', display:'flex', alignItems:'center', justifyContent:'center', marginBottom:'10px', flexShrink:0 }}>
                    <img src={cat.image} alt={cat.label} style={{ width:'72px', height:'72px', objectFit:'contain', display:'block' }}
                      onError={e => { e.target.style.display='none'; e.target.nextSibling.style.display='flex'; }} />
                    <div style={{ display:'none', width:'48px', height:'48px', borderRadius:'10px', backgroundColor:'rgba(204,0,0,0.08)', alignItems:'center', justifyContent:'center', fontSize:'22px' }}>📦</div>
                  </div>
                  <span style={{ fontSize:'clamp(10px,1.2vw,13px)', fontWeight:'600', color: '#111111', textAlign:'center', lineHeight:1.3 }}>{cat.label}</span>
                </Link>
              ))}
            </div>
            <div style={{ textAlign:'center', marginTop:'20px' }}>
              <Link to="/shop" style={{ display:'inline-block', padding:'11px 28px', background:'#CC0000', color:'#ffffff', borderRadius:'4px', fontWeight:'700', fontSize:'13px', textDecoration:'none' }}>View All Products →</Link>
            </div>
          </div>
        </section>

        {/* FEATURED PRODUCTS */}
        {allProducts.length > 0 && (
          <section style={{ backgroundColor: '#ffffff', padding:'clamp(28px,4vw,48px) 0' }}>
            <div style={{ maxWidth:'1280px', margin:'0 auto', padding:'0 clamp(12px,3vw,24px)' }}>
              <div className="home-for-bar">
                <div className="home-for-tabs">
                  <span className="home-for-label" style={{ color: '#111111' }}>FOR</span>
                  {AUDIENCE_TABS.map(tab => (
                    <button key={tab} className="home-for-tab" onClick={() => setActiveTab(tab)}
                      style={{ color: activeTab===tab ? '#CC0000' : (isDark ? 'rgba(255,255,255,0.6)' : '#555555'), borderBottomColor: activeTab===tab ? '#CC0000' : 'transparent' }}>
                      {tab.toUpperCase()}
                    </button>
                  ))}
                </div>
                <div className="home-for-arrows">
                  <button className="home-for-arrow-btn" onClick={() => cycleTab(-1)} aria-label="Previous"
                    style={{ border:`1.5px solid ${isDark ? 'rgba(255,255,255,0.25)' : 'rgba(0,0,0,0.2)'}`, background: isDark ? 'rgba(255,255,255,0.08)' : '#f3f4f6', color: isDark ? '#ffffff' : '#333333' }}>‹</button>
                  <button className="home-for-arrow-btn" onClick={() => cycleTab(1)} aria-label="Next"
                    style={{ border:`1.5px solid ${isDark ? 'rgba(255,255,255,0.25)' : 'rgba(0,0,0,0.2)'}`, background: isDark ? 'rgba(255,255,255,0.08)' : '#f3f4f6', color: isDark ? '#ffffff' : '#333333' }}>›</button>
                </div>
              </div>
              <div className="home-product-grid">
                {(forTagBanners?.[activeTab] || []).map((item, idx) => (
                  <ForTagShowcaseCard key={`${activeTab}-item-${idx}`} item={item} tag={activeTab} isDark={isDark}
                    allItems={forTagBanners?.[activeTab] || []} itemIndex={idx} />
                ))}
                {featuredProducts.map((product) => (
                  <SimpleProductCard key={product.id} product={product} isDark={isDark} />
                ))}
              </div>
            </div>
          </section>
        )}

        {/* WHY CHOOSE US */}
        <section style={{ padding:'clamp(40px,6vw,72px) 0', backgroundColor: '#f7f7f7' }}>
          <div style={{ maxWidth:'1280px', margin:'0 auto', padding:'0 clamp(12px,3vw,24px)' }}>
            <div style={{ textAlign:'center', marginBottom:'clamp(28px,4vw,48px)', display:'flex', flexDirection:'column', alignItems:'center', gap:'10px' }}>
              <span style={{ background:'rgba(204,0,0,0.12)', border:'1px solid rgba(204,0,0,0.3)', color:'#CC0000', padding:'5px 14px', borderRadius:'100px', fontSize:'12px', fontWeight:'600', letterSpacing:'0.05em' }}>Why Techdave?</span>
              <h2 style={{ fontSize:'clamp(22px,4vw,42px)', fontWeight:'800', letterSpacing:'-0.02em', color: '#111111', margin:0 }}>Your Trusted Tech Partner in Harare</h2>
              <p style={{ fontSize:'clamp(13px,1.5vw,16px)', color: isDark ? 'rgba(255,255,255,0.75)' : '#333333', maxWidth:'520px', lineHeight:1.6, margin:0 }}>
                We combine quality products with expert knowledge to deliver the best tech experience in Zimbabwe.
              </p>
            </div>
            <div className="home-why-grid">
              {whyUs.map((item) => (
                <div key={item.title} style={{ backgroundColor: '#ffffff', border:`1px solid ${isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)'}`, borderRadius:'12px', padding:'clamp(18px,2vw,28px)', display:'flex', flexDirection:'column', gap:'10px' }}>
                  <div style={{ width:'48px', height:'48px', borderRadius:'12px', backgroundColor:'rgba(204,0,0,0.08)', border:'1px solid rgba(204,0,0,0.15)', display:'flex', alignItems:'center', justifyContent:'center' }}>
                    {item.icon}
                  </div>
                  <h3 style={{ fontSize:'clamp(15px,1.5vw,18px)', fontWeight:'700', color:'#CC0000', margin:0 }}>{item.title}</h3>
                  <p style={{ fontSize:'clamp(12px,1.2vw,14px)', lineHeight:1.6, color: isDark ? 'rgba(255,255,255,0.75)' : '#333333', margin:0 }}>{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA BANNER */}
        <section style={{ background:'linear-gradient(135deg,#CC0000 0%,#990000 100%)', padding:'clamp(40px,6vw,72px) 0' }}>
          <div style={{ maxWidth:'1280px', margin:'0 auto', padding:'0 clamp(12px,3vw,24px)' }}>
            <div style={{ textAlign:'center', display:'flex', flexDirection:'column', alignItems:'center', gap:'16px' }}>
              <h2 style={{ fontSize:'clamp(22px,4vw,44px)', fontWeight:'900', color:'#ffffff', letterSpacing:'-0.02em', margin:0 }}>Ready to upgrade your tech?</h2>
              <p style={{ fontSize:'clamp(14px,1.5vw,18px)', color:'rgba(255,255,255,0.85)', margin:0 }}>Visit us at Eastgate Market or get in touch today.</p>
              <div className="home-cta-btns">
                <Link to="/shop" style={{ padding:'13px 28px', backgroundColor:'#ffffff', color:'#CC0000', borderRadius:'4px', fontWeight:'800', fontSize:'clamp(13px,1.5vw,15px)', textDecoration:'none', display:'block' }}>Browse Products</Link>
                <Link to="/contact" style={{ padding:'13px 28px', backgroundColor:'transparent', color:'#ffffff', borderRadius:'4px', fontWeight:'700', fontSize:'clamp(13px,1.5vw,15px)', textDecoration:'none', border:'2px solid rgba(255,255,255,0.5)', display:'block' }}>Contact Us →</Link>
              </div>
            </div>
          </div>
        </section>

      </main>
    </>
  );
};

export default Home;
